import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Signal,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { AddOnOverrideSectionComponent } from '@principle-theorem/ng-principle-shared';
import {
  isDisabled$,
  NgDialogModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { Brand, Organisation } from '@principle-theorem/principle-core';
import {
  IOrganisation,
  IOrganisationSubscription,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  ISelectOption,
  isSameRef,
  multiMap,
  multiSwitchMap,
  reduce2DArray,
  toTimestamp,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';

export interface IOrganisationSubscriptionDialogRequest {
  organisation: WithRef<IOrganisation>;
  subscription?: WithRef<IOrganisationSubscription>;
}

export interface IOrganisationSubscriptionDialogResponse {
  subscription?: IOrganisationSubscriptionFormData;
}

export type IOrganisationSubscriptionFormData = Pick<
  IOrganisationSubscription,
  | 'customerId'
  | 'subscriptionId'
  | 'practiceRefs'
  | 'meteredBillingEnabled'
  | 'meteredBillingEnabledAt'
>;

@Component({
  selector: 'pr-organisation-subscription-dialog',
  imports: [
    NgDialogModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatTooltip,
    AddOnOverrideSectionComponent,
  ],
  templateUrl: './organisation-subscription-dialog.component.html',
  styleUrl: './organisation-subscription-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganisationSubscriptionDialogComponent {
  private _dialogRef =
    inject<
      MatDialogRef<
        OrganisationSubscriptionDialogComponent,
        IOrganisationSubscriptionDialogResponse
      >
    >(MatDialogRef);

  request = inject<IOrganisationSubscriptionDialogRequest>(MAT_DIALOG_DATA);
  form = new TypedFormGroup<
    Omit<IOrganisationSubscriptionFormData, 'meteredBillingEnabledAt'>
  >({
    customerId: new TypedFormControl<string>(undefined, [
      Validators.required,
      Validators.pattern(/^cus_/),
    ]),
    subscriptionId: new TypedFormControl<string>(undefined, [
      Validators.required,
      Validators.pattern(/^sub_/),
    ]),
    practiceRefs: new TypedFormControl<DocumentReference<IPractice>[]>([]),
    meteredBillingEnabled: new TypedFormControl<boolean>(false),
  });
  isDisabled$ = isDisabled$(this.form);
  practiceOptions: Signal<ISelectOption<DocumentReference<IPractice>>[]>;
  /**
   * The subscription as it is now, not as it was when the dialog opened, so
   * the add-on overrides tab reflects its own saves and any webhook writes.
   */
  liveSubscription: Signal<WithRef<IOrganisationSubscription> | undefined>;
  isSameRef = isSameRef;

  constructor() {
    const options$ = this._getPracticeOptions$(this.request.organisation);
    this.practiceOptions = toSignal(options$, { initialValue: [] });
    this.liveSubscription = this.request.subscription
      ? toSignal(Firestore.safeDoc$(this.request.subscription.ref), {
          initialValue: this.request.subscription,
        })
      : signal(undefined);
    if (this.request.subscription) {
      this.form.patchValue(this.request.subscription);
    }
  }

  clearPracticeRefs(event: PointerEvent): void {
    event.stopPropagation();
    this.form.patchValue({ practiceRefs: [] });
    this.form.markAsDirty();
    this.form.updateValueAndValidity();
  }

  submit(): void {
    if (!this.form.valid) {
      return;
    }

    const data = this.form.getRawValue();
    const meteredBillingEnabledAt =
      this.request.subscription?.meteredBillingEnabledAt &&
      data.meteredBillingEnabled
        ? this.request.subscription.meteredBillingEnabledAt
        : data.meteredBillingEnabled
          ? toTimestamp()
          : undefined;

    this._dialogRef.close({
      subscription: {
        ...data,
        meteredBillingEnabledAt,
      },
    });
  }

  done(): void {
    this._dialogRef.close();
  }

  private _getPracticeOptions$(
    organisation: WithRef<IOrganisation>
  ): Observable<ISelectOption<DocumentReference<IPractice>>[]> {
    return Organisation.brands$(organisation).pipe(
      multiSwitchMap((brand) =>
        Brand.practices$(brand).pipe(
          multiMap((practice) => ({
            label: `${brand.name} - ${practice.name}`,
            value: practice.ref,
          }))
        )
      ),
      reduce2DArray()
    );
  }
}
